import api from '../api'
import { setToken, setRefreshToken, setUserData, clearAllAuthData } from '../../utils/storage'

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

/**
 * Login user with credentials
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.username - Username or email
 * @param {string} credentials.password - Password
 * @param {boolean} credentials.remember - Remember me option
 * @returns {Promise<Object>} User data and tokens
 */
export const login = async (credentials) => {
  try {
    console.log('🟡 [AuthService] Sending login request to /mobile/auth/login')
    console.log('🟡 [AuthService] Credentials:', { email: credentials.username })

    const response = await api.post('/mobile/auth/login', {
      email: credentials.username, // Laravel expects 'email' not 'username'
      password: credentials.password,
      remember: credentials.remember || false,
    })

    console.log('🟡 [AuthService] Response received:', response.data)

    // Laravel returns data wrapped in "data" object
    const responseData = response.data.data || response.data
    const { access_token, token, user } = responseData

    // Handle both access_token and token (backend uses 'token')
    const authToken = access_token || token

    console.log('🟡 [AuthService] Extracted token:', authToken ? authToken.substring(0, 20) + '...' : 'null')
    console.log('🟡 [AuthService] User data:', user)

    // Store tokens and user data
    setToken(authToken)
    if (responseData.refresh_token) {
      setRefreshToken(responseData.refresh_token)
    }
    setUserData(user)

    console.log('✅ [AuthService] Login successful, returning result')
    return {
      success: true,
      user,
      token: authToken,
    }
  } catch (error) {
    console.error('❌ [AuthService] Login failed:', error)
    console.error('❌ [AuthService] Error response:', error.response?.data)

    // Handle login errors
    const errorMessage = error.response?.data?.message || 'فشل تسجيل الدخول'
    const errors = error.response?.data?.errors || {}

    throw {
      success: false,
      message: errorMessage,
      errors,
      status: error.response?.status,
    }
  }
}

/**
 * Logout current user
 * @returns {Promise<Object>} Logout result
 */
export const logout = async () => {
  try {
    await api.post('/mobile/auth/logout')

    // Clear all stored data
    clearAllAuthData()

    return {
      success: true,
      message: 'تم تسجيل الخروج بنجاح',
    }
  } catch (error) {
    // Even if API call fails, clear local data
    clearAllAuthData()

    return {
      success: true,
      message: 'تم تسجيل الخروج',
    }
  }
}

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export const getProfile = async () => {
  try {
    const response = await api.get('/mobile/auth/profile')

    const user = response.data.data || response.data

    // Update stored user data
    setUserData(user)

    return {
      success: true,
      user,
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'فشل في جلب بيانات المستخدم'

    throw {
      success: false,
      message: errorMessage,
      status: error.response?.status,
    }
  }
}

/**
 * Refresh access token using refresh token
 * @returns {Promise<Object>} New access token
 */
export const refreshToken = async (refresh_token) => {
  try {
    const response = await api.post('/mobile/auth/refresh', {
      refresh_token,
    })

    const { access_token, token } = response.data

    // Handle both access_token and token
    const authToken = access_token || token

    // Update stored access token
    setToken(authToken)

    return {
      success: true,
      token: authToken,
    }
  } catch (error) {
    // Refresh failed, clear all auth data
    clearAllAuthData()

    const errorMessage = error.response?.data?.message || 'انتهت صلاحية الجلسة'

    throw {
      success: false,
      message: errorMessage,
      status: error.response?.status,
    }
  }
}

/**
 * Update user profile
 * @param {Object} profileData - Profile data to update
 * @returns {Promise<Object>} Updated user data
 */
export const updateProfile = async (profileData) => {
  try {
    const response = await api.put('/mobile/auth/profile', profileData)

    const user = response.data.data || response.data

    // Update stored user data
    setUserData(user)

    return {
      success: true,
      user,
      message: 'تم تحديث الملف الشخصي بنجاح',
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'فشل تحديث الملف الشخصي'
    const errors = error.response?.data?.errors || {}

    throw {
      success: false,
      message: errorMessage,
      errors,
      status: error.response?.status,
    }
  }
}

/**
 * Change user password
 * @param {Object} passwordData - Password change data
 * @param {string} passwordData.current_password - Current password
 * @param {string} passwordData.new_password - New password
 * @param {string} passwordData.new_password_confirmation - New password confirmation
 * @returns {Promise<Object>} Password change result
 */
export const changePassword = async (passwordData) => {
  try {
    const response = await api.post('/mobile/auth/change-password', passwordData)

    return {
      success: true,
      message: response.data?.message || 'تم تغيير كلمة المرور بنجاح',
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'فشل تغيير كلمة المرور'
    const errors = error.response?.data?.errors || {}

    throw {
      success: false,
      message: errorMessage,
      errors,
      status: error.response?.status,
    }
  }
}

/**
 * Check if user is authenticated
 * Validates token and fetches user profile
 * @returns {Promise<Object>} Auth status and user data
 */
export const checkAuth = async () => {
  try {
    const response = await api.get('/mobile/auth/profile')

    const user = response.data.data || response.data

    // Update stored user data
    setUserData(user)

    return {
      success: true,
      authenticated: true,
      user,
    }
  } catch (error) {
    // Not authenticated or token invalid
    clearAllAuthData()

    return {
      success: false,
      authenticated: false,
      user: null,
    }
  }
}

// Export all auth service functions
export default {
  login,
  logout,
  getProfile,
  refreshToken,
  updateProfile,
  changePassword,
  checkAuth,
}
